import React, { useEffect, useState } from 'react'
import { colors } from '@/constant/colors';
import { Quest } from '@/constant/schema';
import Image from '@/components/Image';
import WeekDaysSelector from './WeekDaysSelector';
import { Clock, Loader2 } from 'lucide-react';
import { QuestProgress, saveScheduleToDB, Schedule } from '@/constant/schedule';
import { capitalizeFirstLetter } from '@/lib/utils';
import NormalButton from '../Button/normal';
import SimpleSelect from '@/components/ui/SimpleSelect';

interface ScheduleAddCreateProps {
  simple?: boolean;
  questToBeAdded: Quest | null;
  update: boolean;
  questProgress: QuestProgress;
  defaultDays?: number[];
  defaultLessonsPerDay?: number;
  onCreated: () => void;
  isLoadingSchedule?: boolean;
}

const ScheduleAddCreate = ({ questToBeAdded, update = false, questProgress, defaultDays, defaultLessonsPerDay, onCreated, simple = true, isLoadingSchedule = false }: ScheduleAddCreateProps) => {
  const [days, setDays] = useState<number[]>([]);
  const [lessonsPerDay, setLessonsPerDay] = useState(1);
  const [estimatedCompletionTime, setEstimatedCompletionTime] = useState({ weeks: 0, months: 0, weeks_left: 0 });
  const [loading, setLoading] = useState(false);

  const handleCreateSchedule = async () => {
    if (days.length === 0 || loading || !questToBeAdded) return;
    const schedule: Schedule = {
      quest_id: questToBeAdded!.id,
      quest_name: questToBeAdded!.title,
      quest_image: questToBeAdded!.cover_image || '',
      quest_subject: questToBeAdded!.subject,
      quest_progress: questProgress,
      lessons_per_day: lessonsPerDay,
      type: 'custom',
      days: days,
    };
    setLoading(true);
    await saveScheduleToDB(schedule);
    setLoading(false);
    onCreated();
  }

  const estimateCompletionTime = () => {
    const incompleteLessons = questProgress.lesson_total - questProgress.lesson_progress;
    const lessonsPerWeek = days.length * lessonsPerDay;
    const weeks = Math.ceil(incompleteLessons / lessonsPerWeek);
    const months = Math.floor(weeks / 4);
    return { weeks, months, weeks_left: weeks - months * 4 };
  }

  useEffect(() => {
    defaultDays && setDays(defaultDays);
    defaultLessonsPerDay && setLessonsPerDay(defaultLessonsPerDay);
  }, [defaultDays, defaultLessonsPerDay]);

  useEffect(() => {
    setEstimatedCompletionTime(estimateCompletionTime());
  }, [days, lessonsPerDay]);

  if (simple) {
    return (
      <div className="flex flex-col gap-5 w-85"  >
        <span className="emphasize-text text-xl" style={{ color: colors.mainTextColor }}>Set Up Your Learning Plan</span>
        <span className="display-text text-sm text-gray-600">📅 Let's build your learning schedule!<br /> ✅ Complete tasks and get bonus gems! 💎</span>
        <div className="w-full flex flex-col p-4 gap-2 rounded-2xl" style={{ backgroundColor: colors.homeBgColor }}>
          {isLoadingSchedule ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : (
            <>
              <div className="flex flex-row gap-2">
                <Image className="w-fit h-23" src={questToBeAdded?.cover_image || ''} alt={questToBeAdded?.title || ''} width={100} height={100} />
                <div className="flex flex-col gap-1">
                  <span className="font-bold text-sm text-gray-600 ml-4">Pick your study days</span>
                  <WeekDaysSelector small={true} days={days} type={'custom'} className="ml-4 mt-0" onChange={setDays} />
                  <SimpleSelect
                    className="w-48 ml-4 mt-2"
                    value={lessonsPerDay.toString()}
                    onChange={(value) => setLessonsPerDay(parseInt(value, 10))}
                    options={[
                      { value: '1', label: '1 lesson per day' },
                      { value: '2', label: '2 lessons per day' },
                      { value: '3', label: '3 lessons per day' },
                      { value: '4', label: '4 lessons per day' },
                      { value: '5', label: '5 lessons per day' }
                    ]}
                  />
                </div>
              </div>
              <div className="flex flex-row w-full justify-center mt-2">
                <NormalButton
                  onClick={handleCreateSchedule}
                  disabled={days.length === 0 || loading}
                >
                  <span className="text-sm">{loading ? <Loader2 className="w-5 h-5 animate-spin" /> : update ? 'Update Schedule' : 'Create Schedule'}</span>
                </NormalButton>
              </div>
            </>
          )}
        </div>
      </div>
    )
  } else {
    return (
      <div className="flex flex-col gap-5 w-100">
        <span className="emphasize-text text-xl" style={{ color: colors.mainTextColor }}>Set Up Your Learning Plan</span>
        <span className="display-text text-sm text-gray-600">📅 Let's build your learning schedule!<br /> ✅ New daily tasks start today! Complete them to earn more bonus gems 💎</span>
        {isLoadingSchedule ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : (
          <>
            <div className="w-full flex flex-col p-4 gap-2" style={{ backgroundColor: colors.homeBgColor }}>
              <span className="emphasize-text text-base text-gray-600">Selected Book</span>
              <div className="flex flex-row gap-2 ml-4">
                <Image className="w-fit h-12" src={questToBeAdded?.cover_image || ''} alt={questToBeAdded?.title || ''} width={100} height={100} />
                <div className="flex flex-col gap-1">
                  <span className="display-text-bold font-bold text-base text-black clamp-line-2">{questToBeAdded?.title || ''}</span>
                  <span className="display-text text-sm text-gray-600">{capitalizeFirstLetter(questToBeAdded?.subject || '')}</span>
                </div>
              </div>
              <span className="emphasize-text text-base text-gray-600 mt-4">Pick your study days</span>
              <WeekDaysSelector days={days} type={'custom'} className="ml-4 mt-2" onChange={setDays} />
              <span className="emphasize-text text-base text-gray-600 mt-4">Set Daily Goals</span>
              <SimpleSelect
                className="w-48 ml-4"
                value={lessonsPerDay.toString()}
                onChange={(value) => setLessonsPerDay(parseInt(value, 10))}
                options={[
                  { value: '1', label: '1 lesson per day' },
                  { value: '2', label: '2 lessons per day' },
                  { value: '3', label: '3 lessons per day' },
                  { value: '4', label: '4 lessons per day' },
                  { value: '5', label: '5 lessons per day' }
                ]}
              />
              <span className="emphasize-text text-base text-gray-600 mt-4">Estimated Completion Time</span>
              <span className="flex flex-row ml-4" style={{ color: colors.mainTextColor }}>
                <Clock className="w-5 h-5 inline-block align-middle" />
                {days.length > 0 && estimatedCompletionTime.months > 0 && <span className="text-sm text-gray-600 ml-2">{estimatedCompletionTime.months} {estimatedCompletionTime.months === 1 ? 'month' : 'months'}</span>}
                {days.length > 0 && estimatedCompletionTime.weeks_left > 0 && <span className="text-sm text-gray-600">, {estimatedCompletionTime.weeks_left} {estimatedCompletionTime.weeks_left === 1 ? 'week' : 'weeks'}</span>}
              </span>
            </div>
            <div className="flex flex-row w-full justify-center">
              <NormalButton
                onClick={handleCreateSchedule}
                disabled={days.length === 0 || loading}
              >
                <span className="text-sm">{loading ? <Loader2 className="w-5 h-5 animate-spin" /> : update ? 'Update Schedule' : 'Create Schedule'}</span>
              </NormalButton>
            </div>
          </>
        )}
      </div>
    )
  }
}

export default ScheduleAddCreate